import React, { useEffect, useState } from "react";

import EventService from "service/event/EventService";
import LabInvestigationService from "./LabInvestigationService";
import DigitiseModal from "./DigitiseModal";
import { LI_V2_DIGITISE } from "./events";

/**
 * "Digitise report", openable where the lab LIST is not mounted.
 *
 * LabInvestigations owns a DigitiseModal of its own and opens it on
 * LI_V2_DIGITISE — which is enough on the patient screen, where that section is
 * rendered. Create Rx on a phone has no lab list to own one, so its "+ Add" on
 * the Lab investigations card reaches this instead. It is mounted only there
 * (MobilePrescriptionRoute); on any screen with the section mounted this would
 * be a second listener opening a second modal.
 *
 * WHAT IT HAS TO SUPPLY THAT THE SECTION GETS FOR FREE:
 *
 *   · `patientId` and `appointmentId` arrive on the event, from the screen that
 *     knows them — the route this is mounted in does not.
 *   · `docVersion` is READ HERE. The section passes the version of the list it
 *     already loaded, and this has no list, so the lab document is read once
 *     per opening — the same call the section makes. The modal opens at once
 *     and the version lands in a moment; nothing reads it until Save. Should
 *     the read fail, the save is refused rather than written with a wrong
 *     version, and the dialog says it could not save.
 *
 *     Since the uniform OCC change (DigitiseManuallyCard.js — the batch save
 *     is append-only and sends the `expectedVersion: -1` sentinel) the value
 *     no longer has to be CORRECT for the save to land. It is still read
 *     because the card treats `typeof docVersion === "number"` as the mark of
 *     standalone mode; the read is kept as-is rather than trimmed here, since
 *     that is the section's call to make, not the launcher's.
 */
const DigitiseLauncher = () => {
  const [target, setTarget] = useState(null); // { patientId, appointmentId }
  const [docVersion, setDocVersion] = useState(0);

  useEffect(() => {
    const open = (payload) => {
      const patientId = payload && payload.patientId;
      if (!patientId) return;
      setDocVersion(0);
      setTarget({ patientId, appointmentId: payload.appointmentId });
    };
    EventService.addListener(LI_V2_DIGITISE, open);
    return () => EventService.removeListener(LI_V2_DIGITISE, open);
  }, []);

  useEffect(() => {
    if (!target) return undefined;
    let live = true;
    new LabInvestigationService().list(
      target.patientId,
      (res) => {
        if (live) setDocVersion((res && res.version) || 0);
      },
      () => {}
    );
    return () => {
      live = false;
    };
  }, [target]);

  if (!target) return null;

  return (
    <DigitiseModal
      patientId={target.patientId}
      appointmentId={target.appointmentId}
      docVersion={docVersion}
      onClose={() => setTarget(null)}
    />
  );
};

export default DigitiseLauncher;
